import Highcharts from "highcharts";
import { toSafeNumber, toPeriodLabel } from "../data/transformers";
import { BAR_CHART } from "src/config/charts/barChartConfig";

type BasicBarPoint = {
  name?: string;
  value?: number | string;
  percentage?: number | string;
};

type SeriesBarPoint = {
  name?: string;
  y?: number | string;
  percentage?: number | string;
  drilldown?: string;
};

type SeriesBarPayload = {
  series?: Array<{ name?: string; data?: SeriesBarPoint[] }>;
};

type StackedBarSummaryInput = {
  total_count?: number | string;
  label?: string;
  completed_with_sla?: number | string;
  sla_percentage?: string;
};

type TimeSeriesBarPoint = {
  x?: string;
  total?: number | string;
  completed_with_sla?: number | string;
  completed_without_sla?: number | string;
};

type TimeSeriesBarSeriesDataPoint = {
  x?: string;
  y?: number | string;
};

type TimeSeriesBarSeriesInput = {
  name?: string;
  data?: TimeSeriesBarSeriesDataPoint[];
};

type TooltipContext = {
  y?: number;
  series?: {
    name?: string;
  };
};

const getSingleYAxis = (): Highcharts.YAxisOptions => {
  if (Array.isArray(BAR_CHART.yAxis)) {
    return (BAR_CHART.yAxis[0] ?? {}) as Highcharts.YAxisOptions;
  }

  return (BAR_CHART.yAxis ?? {}) as Highcharts.YAxisOptions;
};

const isStackedBarSummaryPayload = (
  value: unknown,
): value is StackedBarSummaryInput => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const payload = value as Record<string, unknown>;

  return (
    "total_count" in payload &&
    "completed_with_sla" in payload &&
    "label" in payload
  );
};

const normalizeSeriesName = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const extractTimeSeriesRows = (rawData: unknown) => {
  if (!Array.isArray(rawData) || rawData.length === 0) {
    return [] as Array<{
      periodKey: string;
      periodLabel: string;
      withSla: number;
      withoutSla: number;
      total: number;
    }>;
  }

  const firstItem = rawData[0] as Record<string, unknown> | undefined;

  if (
    firstItem &&
    typeof firstItem === "object" &&
    "x" in firstItem &&
    ("completed_with_sla" in firstItem || "completed_without_sla" in firstItem)
  ) {
    return (rawData as TimeSeriesBarPoint[])
      .map((entry) => {
        const periodKey = String(entry.x ?? "").trim();
        if (!periodKey) {
          return null;
        }

        const withSla = Math.max(0, toSafeNumber(entry.completed_with_sla));
        const withoutSla = Math.max(
          0,
          toSafeNumber(entry.completed_without_sla),
        );
        const computedTotal = withSla + withoutSla;

        return {
          periodKey,
          periodLabel: toPeriodLabel(periodKey),
          withSla,
          withoutSla,
          total: Math.max(computedTotal, toSafeNumber(entry.total)),
        };
      })
      .filter(Boolean) as Array<{
      periodKey: string;
      periodLabel: string;
      withSla: number;
      withoutSla: number;
      total: number;
    }>;
  }

  const namedSeries = rawData as TimeSeriesBarSeriesInput[];
  const totalSeries = namedSeries.find((entry) => {
    const normalizedName = normalizeSeriesName(entry.name);
    return (
      normalizedName.includes("total") ||
      normalizedName.includes("bv completed")
    );
  });
  const withinSlaSeries = namedSeries.find((entry) => {
    const normalizedName = normalizeSeriesName(entry.name);
    return (
      normalizedName.includes("within sla") ||
      normalizedName.includes("with sla")
    );
  });
  const withoutSlaSeries = namedSeries.find((entry) => {
    const normalizedName = normalizeSeriesName(entry.name);
    return (
      normalizedName.includes("without sla") ||
      normalizedName.includes("outside sla")
    );
  });

  const dataByTimestamp = new Map<
    string,
    { total?: number; withSla?: number; withoutSla?: number }
  >();

  const orderedKeys: string[] = [];

  const mergeSeriesPoints = (
    points: TimeSeriesBarSeriesDataPoint[] | undefined,
    field: "total" | "withSla" | "withoutSla",
  ) => {
    (points ?? []).forEach((point) => {
      const periodKey = String(point.x ?? "").trim();
      if (!periodKey) {
        return;
      }

      if (!dataByTimestamp.has(periodKey)) {
        orderedKeys.push(periodKey);
      }

      const previous = dataByTimestamp.get(periodKey) ?? {};
      previous[field] = Math.max(0, toSafeNumber(point.y));
      dataByTimestamp.set(periodKey, previous);
    });
  };

  mergeSeriesPoints(totalSeries?.data, "total");
  mergeSeriesPoints(withinSlaSeries?.data, "withSla");
  mergeSeriesPoints(withoutSlaSeries?.data, "withoutSla");

  return orderedKeys.map((periodKey) => {
    const values = dataByTimestamp.get(periodKey) ?? {};
    const withSla = Math.max(0, toSafeNumber(values.withSla));
    const explicitWithoutSla = Math.max(0, toSafeNumber(values.withoutSla));
    const total = Math.max(0, toSafeNumber(values.total));

    if (values.total !== undefined && values.withoutSla === undefined) {
      return {
        periodKey,
        periodLabel: toPeriodLabel(periodKey),
        withSla,
        withoutSla: Math.max(0, total - withSla),
        total,
      };
    }

    if (values.total === undefined) {
      return {
        periodKey,
        periodLabel: toPeriodLabel(periodKey),
        withSla,
        withoutSla: explicitWithoutSla,
        total: withSla + explicitWithoutSla,
      };
    }

    return {
      periodKey,
      periodLabel: toPeriodLabel(periodKey),
      withSla,
      withoutSla: explicitWithoutSla,
      total: Math.max(total, withSla + explicitWithoutSla),
    };
  });
};

const buildTimeSeriesBarOptions = (
  normalizedPoints: Array<{
    periodKey: string;
    periodLabel: string;
    withSla: number;
    withoutSla: number;
    total: number;
  }>,
) => {
  const pointCount = normalizedPoints.length;
  const isLowDensity = pointCount <= 8;
  const isMediumDensity = pointCount > 8 && pointCount <= 16;

  const columnPointPadding = isLowDensity
    ? 0.01
    : isMediumDensity
      ? 0.02
      : 0.03;
  const columnGroupPadding = isLowDensity
    ? 0.015
    : isMediumDensity
      ? 0.025
      : 0.04;
  const columnPointWidth = isLowDensity ? undefined : isMediumDensity ? 22 : 18;
  const columnMaxPointWidth = isLowDensity ? 56 : isMediumDensity ? 34 : 24;

  const latestChartOptions = {
    ...BAR_CHART,
    chart: {
      ...BAR_CHART.chart,
      type: "column",
      // Only enable horizontal scroll when there are many bars.
      ...(pointCount > 16
        ? {
            scrollablePlotArea: {
              minWidth: Math.max(900, normalizedPoints.length * 56),
              scrollPositionX: 1,
            },
          }
        : {}),
    },
  };

  latestChartOptions.xAxis = {
    ...(BAR_CHART.xAxis ?? {}),
    type: "category",
    categories: normalizedPoints.map((point) => point.periodLabel),
    labels: {
      rotation: normalizedPoints.length > 12 ? -35 : 0,
      style: {
        fontSize: "11px",
      },
    },
  };

  latestChartOptions.yAxis = {
    ...getSingleYAxis(),
    min: 0,
    title: {
      ...(getSingleYAxis().title ?? {}),
      text: "Count",
    },
  };

  latestChartOptions.plotOptions = {
    ...(BAR_CHART.plotOptions ?? {}),
    column: {
      pointPadding: columnPointPadding,
      groupPadding: columnGroupPadding,
      borderWidth: 0,
      pointWidth: columnPointWidth,
      maxPointWidth: columnMaxPointWidth,
      minPointLength: 2,
    },
    series: {
      ...(BAR_CHART.plotOptions?.series ?? {}),
      stacking: "normal",
      borderWidth: 0,
      dataLabels: {
        enabled: false,
      },
    },
  };

  latestChartOptions.legend = {
    enabled: true,
    align: "center",
    verticalAlign: "bottom",
    layout: "horizontal",
  };

  latestChartOptions.tooltip = {
    shared: true,
    useHTML: true,
    formatter: function formatter(this: any) {
      const hoveredLabel = String(
        this.points?.[0]?.point?.category ?? this.x ?? "",
      );
      const total = (this.points ?? []).reduce(
        (sum: number, point: any) => sum + toSafeNumber(point.y),
        0,
      );

      const rows = (this.points ?? [])
        .map(
          (point: any) =>
            `<div style="display:flex; justify-content:space-between; gap:12px; font-size:12px; margin-top:2px;"><span><span style="color:${point.color}">●</span> ${point.series.name}</span><b>${toSafeNumber(point.y)}</b></div>`,
        )
        .join("");

      return `<div style="padding:8px 10px; min-width:200px;"><div style="font-size:12px; font-weight:700; margin-bottom:4px;">${hoveredLabel}</div>${rows}<div style="margin-top:6px; font-size:11px; opacity:0.85;">Total: <b>${total}</b></div></div>`;
    },
  };

  latestChartOptions.series = [
    {
      type: "column",
      name: "Completed With SLA",
      color: "#22C55E",
      data: normalizedPoints.map((point) => point.withSla),
    },
    {
      type: "column",
      name: "Completed Without SLA",
      color: "#F59E0B",
      data: normalizedPoints.map((point) => point.withoutSla),
    },
  ];

  return latestChartOptions;
};

const buildStackedBarOptions = (stackedData: StackedBarSummaryInput) => {
  const totalCount = Math.max(0, toSafeNumber(stackedData.total_count));
  const completedWithSla = Math.min(
    totalCount,
    Math.max(0, toSafeNumber(stackedData.completed_with_sla)),
  );
  const outsideSla = Math.max(0, totalCount - completedWithSla);
  const label = String(stackedData.label ?? "Total");
  const fallbackSlaPercentage =
    totalCount > 0
      ? `${((completedWithSla / totalCount) * 100).toFixed(2)}%`
      : "0%";
  const slaPercentage = String(
    stackedData.sla_percentage ?? fallbackSlaPercentage,
  );

  const latestChartOptions = {
    ...BAR_CHART,
    chart: {
      ...BAR_CHART.chart,
      type: "bar",
    },
  };

  latestChartOptions.xAxis = {
    ...(BAR_CHART.xAxis ?? {}),
    categories: [label],
  };

  latestChartOptions.yAxis = {
    ...getSingleYAxis(),
    max: totalCount,
    title: {
      ...(getSingleYAxis().title ?? {}),
      text: "Count",
    },
  };

  latestChartOptions.plotOptions = {
    ...(BAR_CHART.plotOptions ?? {}),
    series: {
      ...(BAR_CHART.plotOptions?.series ?? {}),
      stacking: "normal",
      borderWidth: 0,
      dataLabels: {
        enabled: true,
        inside: true,
        style: {
          fontSize: "12px",
          fontWeight: "700",
          textOutline: "none",
        },
      },
    },
  };

  latestChartOptions.legend = {
    enabled: true,
    align: "center",
    verticalAlign: "bottom",
    layout: "horizontal",
  };

  latestChartOptions.tooltip = {
    shared: false,
    split: false,
    useHTML: true,
    formatter: function formatter(this: TooltipContext) {
      const pointValue = toSafeNumber(this.y);
      const seriesName = String(this.series?.name ?? "Series");
      return `
        <div style="padding:8px 10px; min-width:170px;">
          <div style="font-size:12px; font-weight:700; margin-bottom:4px;">${seriesName}</div>
          <div style="font-size:12px; margin-bottom:2px;">Count: <b>${pointValue}</b></div>
          <div style="font-size:11px; opacity:0.85;">Total: <b>${totalCount}</b> | SLA: <b>${slaPercentage}</b></div>
        </div>
      `;
    },
  };

  latestChartOptions.series = [
    {
      type: "bar",
      name: "Completed With SLA",
      color: "#22C55E",
      data: [completedWithSla],
    },
    {
      type: "bar",
      name: "Outside SLA",
      color: "#F59E0B",
      data: [outsideSla],
    },
  ];

  return latestChartOptions;
};

// Populate line chart options with x-axis and series data
const buildBarOptions = (barChartData: unknown) => {
  if (isStackedBarSummaryPayload(barChartData)) {
    return buildStackedBarOptions(barChartData);
  }

  const timeSeriesRows = extractTimeSeriesRows(barChartData);
  if (timeSeriesRows.length > 0) {
    return buildTimeSeriesBarOptions(timeSeriesRows);
  }

  // Detect {series: [{data: [{name, y, percentage}]}]} shape (pie_drilldown-style API)
  const isSeriesPayload =
    !Array.isArray(barChartData) &&
    barChartData !== null &&
    typeof barChartData === "object" &&
    Array.isArray((barChartData as SeriesBarPayload).series);

  let points: Array<{ name: string; value: number; percentage: number }> = [];

  if (isSeriesPayload) {
    const rawPoints =
      (barChartData as SeriesBarPayload).series?.[0]?.data ?? [];
    points = rawPoints.map((p) => ({
      name: String(p.name ?? ""),
      value: toSafeNumber(p.y),
      percentage: toSafeNumber(p.percentage),
    }));
  } else {
    const flat = Array.isArray(barChartData)
      ? (barChartData as BasicBarPoint[])
      : [];
    const total = flat.reduce((sum, p) => sum + toSafeNumber(p.value), 0);
    points = flat.map((p) => ({
      name: String(p.name ?? ""),
      value: toSafeNumber(p.value),
      // Use the API-supplied percentage when present (e.g. approval rate,
      // PA required rate) — only fall back to share-of-total for row shapes
      // that never carry one (map/data_table/funnel conversions).
      percentage:
        p.percentage !== undefined
          ? toSafeNumber(p.percentage)
          : total > 0
            ? (toSafeNumber(p.value) / total) * 100
            : 0,
    }));
  }

  let latestChartOptions = {
    ...BAR_CHART,
    chart: {
      ...BAR_CHART.chart,
      marginRight: 70,
    },
  };

  latestChartOptions.xAxis = {
    categories: points.map((p) => p.name),
  };
  latestChartOptions.series = [
    {
      type: "bar",
      name: "Value",
      dataLabels: {
        enabled: true,
        align: "left",
        inside: false,
        // crop: false,
        // overflow: "allow" as const,
        x: 5,
        style: {
          fontSize: "11px",
          fontWeight: "600",
          textOutline: "none",
          color: "#555",
        },
        formatter: function (this: any) {
          const idx = this.point?.index ?? 0;
          const pct = points[idx]?.percentage ?? 0;
          return `${pct.toFixed(1)}%`;
        },
      },
      data: points.map((p) => p.value),
    },
  ];

  return latestChartOptions;
};

export const renderBar = (
  data: unknown,
  spec: unknown,
  title: string,
): Highcharts.Options => {
  const options = buildBarOptions(data);
  return {
    ...options,
    // title: {
    //   text: title,
    //   align: "left",
    // },
  };
};
